import { IExtensionFolio, ISubExtension } from '../../models/Extension';

// The rules a folio has to satisfy, in one place, because both editing modes need them and they must not
// drift apart: the form uses them to decide whether Save is allowed, and raw mode shows them as a linter.
//
// Errors mirror what `FolioAdminHandler.Validate` rejects server side. They are duplicated here to turn a
// round-trip 400 into an inline message, NOT to replace the server check — the server still runs its own,
// and it is the one that counts.
//
// Warnings are ours alone. They are the mistakes that produce a folio which saves cleanly and then
// disappoints someone: a connector nobody can get credentials for, a secret stored in the clear, a folio
// that installs with nothing switched on.

export type FolioIssueLevel = 'error' | 'warning';

export interface IFolioIssue {
    level: FolioIssueLevel;
    /** Where the problem is, in terms the author can find: 'Shared fields', 'importIngramFile', … */
    where?: string;
    message: string;
}

/** Field names that almost certainly hold a secret. Used to catch an unprotected one. */
const SECRET_HINTS = ['password', 'secret', 'apikey', 'privatekey', 'token'];

const looksSecret = (fieldName: string): boolean => {
    const normalized = fieldName.replace(/[_-]/g, '').toLowerCase();
    return SECRET_HINTS.some(hint => normalized.includes(hint));
};

/** Parses the raw document. Returns the folio, or the parse error that stopped it. */
export const parseFolioDocument = (text: string): { folio?: IExtensionFolio, error?: string } => {
    let value: unknown;
    try {
        value = JSON.parse(text);
    } catch (e) {
        return { error: (e as Error).message };
    }
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return { error: 'The definition must be a JSON object.' };
    }
    return { folio: value as IExtensionFolio };
};

const validateSubExtension = (key: string, sub: ISubExtension | undefined): IFolioIssue[] => {
    const issues: IFolioIssue[] = [];
    const where = key;
    if (!sub || typeof sub !== 'object') {
        return [{ level: 'error', where, message: 'This capability is not an object.' }];
    }
    if (!sub.identifier) {
        issues.push({ level: 'error', where, message: 'Missing an identifier.' });
    } else if (sub.identifier !== key) {
        // Installed credential values are stored per identifier, so a key that disagrees with the
        // identifier it holds routes one capability's credentials to another.
        issues.push({
            level: 'error', where,
            message: `The key and the identifier disagree ("${key}" vs "${sub.identifier}"). Stored credentials are keyed by identifier, so these must match.`
        });
    }
    if (!sub.name?.trim()) issues.push({ level: 'error', where, message: 'Needs a name.' });
    // Warnings, not errors: the server accepts both, and published folios already carry capabilities
    // that run on extensionType alone. Blocking them would make those folios unsaveable from here.
    if (!sub.category?.trim()) {
        issues.push({
            level: 'warning', where,
            message: sub.extensionType?.trim()
                ? `Has no category; it is grouped by its extension type ("${sub.extensionType}") instead.`
                : 'Has no category or extension type, so nothing says what kind of capability it is.'
        });
    }

    // A capability with no URL has to say what drives it instead, or it installs and never runs. A
    // direct import is the exception: its fields each call their own endpoint, and the capability's own
    // url is empty on every one that exists.
    if (!sub.url?.trim() && !sub.subCategory?.trim() && sub.category !== 'directImport') {
        issues.push({
            level: 'warning', where,
            message: 'Has no url. Give it one, or set a sub category (such as import_configurator) to say what runs it instead.'
        });
    }

    if (sub.url?.trim() && sub.url.includes('localhost')) {
        issues.push({
            level: 'warning', where,
            message: 'Points at localhost. This will install cleanly and fail for every organization.'
        });
    }

    if (!sub.helpLinks?.length) {
        issues.push({
            level: 'warning', where,
            message: 'No credential documentation. One connector often issues different credentials per capability, and this is where an operator looks for how to get these.'
        });
    }

    for (const field of sub.additionalData ?? []) {
        if (field.field && looksSecret(field.field) && !field.protected) {
            issues.push({
                level: 'warning', where,
                message: `"${field.field}" looks like a secret but is not marked protected, so it will be readable back out of the settings screen.`
            });
        }
    }

    issues.push(...validateValueMaps(where, sub));

    return issues;
};

/**
 * Errors mirror FolioAdminHandler.ValidateValueMaps: a row with no field or file value matches nothing, and two
 * rows for one value make which wins depend on their order.
 */
const validateValueMaps = (where: string, sub: ISubExtension): IFolioIssue[] => {
    const issues: IFolioIssue[] = [];
    const maps = sub.valueMaps ?? [];
    const seen = new Set<string>();
    for (const map of maps) {
        const field = map.field?.trim() ?? '';
        const from = map.from?.trim() ?? '';
        if (!field || !from) {
            issues.push({ level: 'error', where, message: 'A value translation needs a field and a value in the file.' });
            continue;
        }
        const key = `${field.toLowerCase()}|${from.toLowerCase()}`;
        if (seen.has(key)) {
            issues.push({ level: 'error', where, message: `"${from}" in ${field} is translated more than once.` });
        }
        seen.add(key);
        if (!map.to?.trim()) {
            issues.push({ level: 'warning', where, message: `"${from}" in ${field} translates to nothing, so the importer reads it as blank.` });
        }
    }
    if (maps.length && sub.category !== 'import') {
        issues.push({ level: 'warning', where, message: 'Has value translations, but only import capabilities use them.' });
    }
    return issues;
};

/** The quote-link declaration, when the folio has one. Absent is fine: most folios offer no quote link. */
const validateQuoteLink = (folio: IExtensionFolio, capabilityKeys: string[]): IFolioIssue[] => {
    const quoteLink = folio.quoteLink;
    if (!quoteLink) return [];
    const where = 'Quote link';
    const issues: IFolioIssue[] = [];
    if (!quoteLink.subExtension?.trim()) {
        issues.push({ level: 'warning', where, message: 'Pick the capability the quote link runs, or switch the quote link off.' });
    } else if (!capabilityKeys.includes(quoteLink.subExtension)) {
        // The link is built from this identifier, so one that names nothing opens a capability that
        // does not exist and the button fails for every operator who added it. A warning, not an error,
        // only because the server does not check quote links and errors here mirror the server.
        issues.push({ level: 'warning', where, message: `Runs "${quoteLink.subExtension}", which is not a capability of this folio.` });
    } else if (folio.subExtensions?.[quoteLink.subExtension]?.category !== 'directImport') {
        issues.push({ level: 'warning', where, message: `Runs "${quoteLink.subExtension}", which is not a direct-import capability.` });
    }
    if (!quoteLink.parameter?.trim()) {
        issues.push({
            level: 'warning', where,
            message: 'No query string, so the link does not say which quote was clicked and the operator has to pick it by hand.'
        });
    } else if (quoteLink.parameter.trim().startsWith('?')) {
        issues.push({ level: 'warning', where, message: 'Leave the leading "?" off the query string; the link adds it.' });
    }
    if (!(quoteLink.instructions ?? []).some(step => step.trim())) {
        issues.push({ level: 'warning', where, message: 'No setup steps, so operators are not told where in their PSA to add the button.' });
    }
    if (!!quoteLink.permissionUrl?.trim() !== !!quoteLink.createUrl?.trim()) {
        issues.push({ level: 'warning', where, message: 'Automatic install needs both the permission URL and the create URL.' });
    }
    return issues;
};

/**
 * Every problem with this folio. Save is blocked while any issue is an error; warnings are shown and can
 * be saved past, because some of them are legitimate for an unusual connector.
 */
export const validateFolio = (folio: IExtensionFolio | undefined, id: string): IFolioIssue[] => {
    const issues: IFolioIssue[] = [];
    if (!folio) return [{ level: 'error', message: 'There is no folio to save.' }];

    if (!id.trim()) issues.push({ level: 'error', message: 'A folio id is required.' });
    if (folio.id && id.trim() && folio.id !== id.trim()) {
        issues.push({
            level: 'error',
            message: `The id in the document ("${folio.id}") does not match the folio id ("${id.trim()}").`
        });
    }
    if (!folio.name?.trim()) {
        issues.push({ level: 'error', message: 'The folio needs a name; it is what operators pick from.' });
    }

    const subs = folio.subExtensions ?? {};
    const keys = Object.keys(subs);
    if (keys.length === 0) {
        issues.push({ level: 'error', message: 'A folio needs at least one capability; an empty folio installs nothing.' });
    }
    for (const key of keys) issues.push(...validateSubExtension(key, subs[key]));

    for (const identifier of folio.defaultSubExtensions ?? []) {
        if (!keys.includes(identifier)) {
            issues.push({
                level: 'error',
                message: `"${identifier}" is switched on by default but is not a capability of this folio.`
            });
        }
    }

    for (const field of folio.sharedFields ?? []) {
        if (field.field && looksSecret(field.field) && !field.protected) {
            issues.push({
                level: 'warning', where: 'Common settings',
                message: `"${field.field}" looks like a secret but is not marked protected, so it will be readable back out of the settings screen.`
            });
        }
    }

    issues.push(...validateQuoteLink(folio, keys));

    if (keys.length > 0 && !(folio.defaultSubExtensions ?? []).length) {
        issues.push({
            level: 'warning',
            message: 'Nothing is switched on by default, so a one-click setup of this folio enables nothing.'
        });
    }
    if (!folio.image?.trim()) {
        issues.push({ level: 'warning', message: 'No logo. The picker will fall back to a generic icon.' });
    }

    return issues;
};

export const hasErrors = (issues: IFolioIssue[]): boolean => issues.some(i => i.level === 'error');
